<template>
  <BaseDialog
    :title="dialogTitle"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4 pb-2">
      <BaseSwitchListItem
        v-model="includeTeacherAssessment"
        class="bg-surface-container-low rounded-large"
        :title="$t('lumia.lesson.feedback.includeTeacherAssessment')"
        :supporting-text="$t('lumia.lesson.feedback.includeTeacherAssessmentHint')"
      />
      <p v-if="isChange" class="text-body-medium text-on-surface-variant">
        {{ $t('lumia.lesson.feedback.changeHint') }}
      </p>
      <p v-if="audienceGroups.length === 0" class="text-body-medium text-on-surface-variant">
        {{ $t('lumia.lesson.feedback.audienceEmpty') }}
      </p>
      <div v-else class="flex flex-col gap-4">
        <section v-for="group in audienceGroups" :key="group.id" class="flex flex-col gap-0.5">
          <div class="flex flex-row items-center">
            <h4 class="text-title-medium text-on-surface-variant grow">{{ group.title }}</h4>
            <BaseTextButton size="XS" @click="toggleGroup(group)">{{
              isGroupIncluded(group) ? $t('common.selectNonePeople') : $t('common.selectAllPeople')
            }}</BaseTextButton>
          </div>
          <ul class="flex flex-col gap-0.5">
            <li
              v-for="member in group.members"
              :key="`${group.id}-${member.id}`"
              class="first:[&>button]:rounded-t-large last:[&>button]:rounded-b-large"
            >
              <BaseCheckboxListItem
                :title="memberFullName(member)"
                :icon="group.isTeam ? undefined : 'person'"
                :selected="isIncluded(member.id)"
                compact
                @click="toggleMember(member.id)"
              >
                <template v-if="group.isTeam" #leading>
                  <BaseMemberAvatar :member="member" size="32" omit-tooltip class="-ml-1" />
                </template>
              </BaseCheckboxListItem>
            </li>
          </ul>
        </section>
      </div>
      <p v-if="isChange && includedIds.length === 0" class="text-body-medium text-error">
        {{ $t('lumia.lesson.feedback.excludeEveryone') }}
      </p>
    </div>
    <template #dismiss>
      <BaseTextButton size="M" color="text" @click="emit('close')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton
        size="M"
        color="filled"
        :disabled="isSubmitting || !canConfirm"
        @click="submit"
      >
        {{ confirmLabel }}
      </BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseSwitchListItem from '@/core/components/base/switch-list-item/BaseSwitchListItem.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import type { Member } from '@/core/models/member/member'
  import { memberFullName } from '@/core/models/member/member-name'
  import type { LessonTeam, StartLessonFeedbackBody } from '../models/lesson'
  import BaseCheckboxListItem from '@/core/components/base/list-item/BaseCheckboxListItem.vue'

  interface AudienceGroup {
    id: string
    title: string
    isTeam: boolean
    members: Member[]
  }

  interface Props {
    isOpen: boolean
    teams: ReadonlyArray<LessonTeam>
    members: ReadonlyArray<Member>
    isSubmitting?: boolean
    /** `change` edits a running feedback. `start` begins one. */
    mode?: 'start' | 'change'
    /** Members already asked. Used when mode is `change`. */
    subjects?: ReadonlyArray<Member>
    /** Whether the teacher currently assesses. Used when mode is `change`. */
    teacherAssesses?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    mode: 'start',
    subjects: () => [],
    teacherAssesses: true,
  })

  const emit = defineEmits<{
    close: []
    start: [body: StartLessonFeedbackBody]
  }>()

  const { t } = useI18n()

  const includedIds = ref<string[]>([])
  const includeTeacherAssessment = ref(true)
  const isChange = computed(() => props.mode === 'change')
  const dialogTitle = computed(() =>
    isChange.value ? t('lumia.lesson.feedback.change') : t('lumia.lesson.feedback.start'),
  )
  const confirmLabel = computed(() =>
    isChange.value ? t('common.save') : t('lumia.lesson.feedback.start'),
  )
  const canConfirm = computed(() => !isChange.value || includedIds.value.length > 0)

  const sortMembers = (members: ReadonlyArray<Member>) =>
    [...members].sort((left, right) =>
      memberFullName(left).localeCompare(memberFullName(right), 'de'),
    )

  const audienceGroups = computed<AudienceGroup[]>(() => {
    const coveredIds = new Set<string>()
    const teams = [...props.teams]
      .sort((left, right) => left.name.localeCompare(right.name, 'de'))
      .flatMap((team) => {
        const members = sortMembers(team.subjects)
        if (members.length === 0) {
          return []
        }
        for (const member of members) {
          coveredIds.add(member.id)
        }
        return [{ id: team.id, title: team.name, isTeam: true, members }]
      })

    const individuals = sortMembers(props.members.filter((member) => !coveredIds.has(member.id)))
    const groups =
      individuals.length === 0
        ? teams
        : [
            ...teams,
            {
              id: 'individuals',
              title: t('lumia.lesson.members'),
              isTeam: false,
              members: individuals,
            },
          ]

    if (!isChange.value) {
      return groups
    }

    const listedIds = new Set(groups.flatMap((group) => group.members.map((member) => member.id)))
    const former = sortMembers(props.subjects.filter((member) => !listedIds.has(member.id)))
    if (former.length === 0) {
      return groups
    }
    return [
      ...groups,
      {
        id: 'former',
        title: t('lumia.lesson.feedback.formerMembers'),
        isTeam: false,
        members: former,
      },
    ]
  })

  const audienceIds = computed(() => {
    const ids = new Set<string>()
    for (const group of audienceGroups.value) {
      for (const member of group.members) {
        ids.add(member.id)
      }
    }
    return [...ids]
  })

  const isIncluded = (memberId: string) => includedIds.value.includes(memberId)

  const isGroupIncluded = (group: AudienceGroup) =>
    group.members.every((member) => isIncluded(member.id))

  const toggleMember = (memberId: string) => {
    if (isIncluded(memberId)) {
      includedIds.value = includedIds.value.filter((id) => id !== memberId)
      return
    }
    includedIds.value = [...includedIds.value, memberId]
  }

  const toggleGroup = (group: AudienceGroup) => {
    const memberIds = group.members.map((member) => member.id)
    if (isGroupIncluded(group)) {
      includedIds.value = includedIds.value.filter((id) => !memberIds.includes(id))
      return
    }
    includedIds.value = [...new Set([...includedIds.value, ...memberIds])]
  }

  watch(
    () => props.isOpen,
    (open) => {
      if (!open) {
        return
      }
      if (isChange.value) {
        includeTeacherAssessment.value = props.teacherAssesses
        includedIds.value = props.subjects.map((member) => member.id)
        return
      }
      includeTeacherAssessment.value = true
      includedIds.value = [...audienceIds.value]
    },
  )

  const submit = () => {
    if (props.isSubmitting || !canConfirm.value) {
      return
    }
    const excludedMemberIds = audienceIds.value.filter((memberId) => !isIncluded(memberId))

    if (isChange.value) {
      emit('start', {
        includeTeacherAssessment: includeTeacherAssessment.value,
        excludedMemberIds,
      })
      return
    }

    emit('start', {
      includeTeacherAssessment: includeTeacherAssessment.value,
      ...(excludedMemberIds.length > 0 ? { excludedMemberIds } : {}),
    })
  }
</script>
